// Capa de fetchers contra la REST del engine (M2). Cada función devuelve
// el dato deserializado y lanza Error en fallos transport-level o status
// >= 400. Los call-sites deciden si fallback al mock (mastersStore) o
// reportar al usuario.
//
// El base URL es relativo ("/api") — Vite proxea a :8080 en dev y en
// prod sirve el SPA desde el mismo binario, así que no hay CORS por
// resolver. Cuando montemos auth (Fase 3 M3), añadimos el header
// Authorization acá una sola vez.

import type {
  Client,
  ClientAddress,
  GpsPing,
  Notification,
  Order,
  OrderDetail,
  Route,
  RouteStopRow,
  SupervisorTeamMember,
  User,
  Vehicle,
  Warehouse,
} from "./schemaTypes";
import { adaptOrderBatch as orderBatchAdapter } from "../features/programacion/orderBatchMapping";
import type {
  BatchOrderRow,
  OrderBatch,
  OrderBatchStatus,
  UploadOrderBatchCsvReq,
  UploadOrderBatchCsvResp,
} from "./orderBatchTypes";
import type {
  CreateOptimizerConfigReq,
  OptimizerConfigProfile,
  UpdateOptimizerConfigReq,
} from "./optimizerConfigTypes";

export type {
  BatchOrderRow,
  OrderBatch,
  OrderBatchStatus,
  UploadOrderBatchCsvReq,
  UploadOrderBatchCsvResp,
};
export type {
  CreateOptimizerConfigReq,
  OptimizerConfigProfile,
  UpdateOptimizerConfigReq,
};

// Espejo de postgres.LastMileImportResult del backend. Counts por entidad.
export type LastMileImportResult = {
  Warehouses: { Created: number; Found: number };
  Clients: { Created: number; Updated: number; Found: number };
  Drivers: { Created: number; Found: number };
  Vehicles: { Created: number; Found: number };
  Orders: { Created: number; Updated: number };
  SalesDocuments: { Created: number; Updated: number };
  Routes: { Created: number; Updated: number };
  RouteStops: { Created: number; Updated: number };
  RowsSkipped: number;
  Errors: string[] | null;
  ImportBatchID: string;
};

// Item del historial de imports (GET /api/last-mile/imports/).
export type LastMileImportSummary = {
  id: string;
  created_at: string; // ISO
  created_by_user_id?: string;
  created_by_full_name?: string;
  source_filename?: string;
  orders_count: number;
  routes_count: number;
  sales_documents_count: number;
  deliveries_count: number;
  exceptions_count: number;
};

// Counts borrados al deshacer un import (DELETE /api/last-mile/imports/{id}).
export type LastMileImportDeleteResult = {
  OrdersDeleted: number;
  RoutesDeleted: number;
  RouteStopsDeleted: number;
  SalesDocumentsDeleted: number;
  DeliveriesDeleted: number;
  ExceptionsDeleted: number;
};

export type ListEnvelope<T> = { data: T[]; count: number };

// --- X-User-ID injection (M6.10) ----------------------------------------
//
// Hasta que JWT (API-3) aterrice, el backend identifica al usuario por
// el header X-User-ID. AuthContext setea este valor al loguear / logout
// llamando `setApiUserId`. Las funciones getJSON / sendJSON lo agregan
// como header en cada request si está presente.
//
// Lo hacemos via variable de módulo (no parameter) porque pasar el user
// por cada call-site sería ruido en 30 archivos. La trade-off es que en
// tests hay que recordar `setApiUserId(null)` entre runs — el setup.ts
// de vitest ya lo hace si querés cobertura.

let currentUserId: string | null = null;

export function setApiUserId(userId: string | null) {
  currentUserId = userId;
}

// resolveTenantSlug — extrae el slug del subdominio actual. Cuando el
// usuario navega a `acme.antmile.pe`, devuelve `"acme"`. Casos:
//   - Subdomain → ese slug.
//   - Dominio raíz (`antmile.pe`, `localhost`, `127.0.0.1`, IP, etc.)
//     → null. El backend cae al X-Account-ID legacy o al default dev.
//   - Override por env (`VITE_TENANT_SLUG=...`) → útil para probar
//     desde localhost contra un backend prod con multi-tenant.
function resolveTenantSlug(): string | null {
  const override = (import.meta.env.VITE_TENANT_SLUG as string | undefined)?.trim();
  if (override) return override;
  if (typeof window === "undefined") return null;
  const host = window.location.hostname;
  // localhost / IP / sin dots → no hay subdomain real.
  if (host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host) || !host.includes(".")) {
    return null;
  }
  const parts = host.split(".");
  // El dominio base esperado es `antmile.pe` (2 partes). Cualquier cosa
  // con >= 3 partes tiene un subdomain — la primera es el slug.
  // Excepción: 'www' no es un slug, lo ignoramos para que `www.antmile.pe`
  // funcione como el raíz.
  if (parts.length >= 3 && parts[0] !== "www") {
    return parts[0];
  }
  return null;
}

// Cache del slug — lo resolvemos una vez al cargar el módulo. La página
// se rebuilea si el usuario navega entre subdomains (window.location
// cambia → reload completo del SPA), así que esto NO se desactualiza.
const tenantSlug = resolveTenantSlug();

function authHeaders(): Record<string, string> {
  const h: Record<string, string> = {};
  if (currentUserId) h["X-User-ID"] = currentUserId;
  // X-Tenant-Slug se manda solo desde el cliente como fallback. En
  // producción nginx también lo setea desde el Host header — el
  // backend prioriza el de nginx porque corre primero en el pipeline.
  if (tenantSlug) h["X-Tenant-Slug"] = tenantSlug;
  return h;
}

/// getTenantSlug — útil para que la UI muestre "Estás en: <slug>" o
/// guarde el slug en localStorage. No tiene side-effects.
export function getTenantSlug(): string | null {
  return tenantSlug;
}

/** Auth headers expuestos para callers que necesitan hacer fetch manual
 *  (CSV uploads, downloads con presigned URLs, etc.). */
export function getAuthHeaders(): Record<string, string> {
  return authHeaders();
}

// Type-safe fetch wrappers. Lanza con detalle del status para que el
// caller pueda mostrar errores legibles ("API 503: DB unavailable").

async function getJSON<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    headers: { Accept: "application/json", ...authHeaders() },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`API ${res.status} on ${path}: ${body || res.statusText}`);
  }
  return res.json() as Promise<T>;
}

async function sendJSON<T>(
  method: "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...authHeaders(),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`API ${res.status} on ${method} ${path}: ${text || res.statusText}`);
  }
  // 204 No Content fine; pero por convención devolvemos JSON del recurso.
  return res.json() as Promise<T>;
}

// --- DTO → domain mapping ------------------------------------------------
//
// El backend devuelve campos snake_case y omite los vacíos. Los tipos del
// front (`schemaTypes.ts`) ya son snake_case, así que el mapeo es casi
// identidad — basta con rellenar defaults para los campos `omitempty`.

const ACCOUNT_ID = "00000000-0000-0000-0000-000000000001";

function adaptUser(raw: Record<string, unknown>): User {
  return {
    id: raw.id as string,
    account_id: ACCOUNT_ID,
    role: raw.role as User["role"],
    email: raw.email as string,
    full_name: raw.full_name as string,
    phone: raw.phone as string | undefined,
    code: raw.code as string | undefined,
    active: raw.active as boolean,
    last_login_at: raw.last_login_at as string | undefined,
  };
}

function adaptWarehouse(raw: Record<string, unknown>): Warehouse {
  return {
    id: raw.id as string,
    account_id: ACCOUNT_ID,
    code: raw.code as string,
    name: raw.name as string,
    address: raw.address as string | undefined,
    lat: raw.lat as number | undefined,
    lon: raw.lon as number | undefined,
    active: raw.active as boolean,
  };
}

function adaptClient(raw: Record<string, unknown>): Client {
  return {
    id: raw.id as string,
    account_id: ACCOUNT_ID,
    code: raw.code as string,
    name: raw.name as string,
    tax_id_type: raw.tax_id_type as Client["tax_id_type"],
    tax_id: raw.tax_id as string | undefined,
    contact_name: raw.contact_name as string | undefined,
    contact_phone: raw.contact_phone as string | undefined,
    email: raw.email as string | undefined,
    address: raw.address as string | undefined,
    district: raw.district as string | undefined,
    city: (raw.city as string) ?? "Lima",
    lat: raw.lat as number | undefined,
    lon: raw.lon as number | undefined,
    business_type: raw.business_type as string | undefined,
    notes: raw.notes as string | undefined,
    active: raw.active as boolean,
  };
}

function adaptClientAddress(raw: Record<string, unknown>): ClientAddress {
  return {
    id: raw.id as string,
    client_id: raw.client_id as string,
    label: raw.label as string,
    address: raw.address as string,
    district: raw.district as string | undefined,
    city: (raw.city as string) ?? "Lima",
    lat: raw.lat as number | undefined,
    lon: raw.lon as number | undefined,
    contact_name: raw.contact_name as string | undefined,
    contact_phone: raw.contact_phone as string | undefined,
    is_primary: raw.is_primary as boolean,
    active: raw.active as boolean,
  };
}

function adaptVehicle(raw: Record<string, unknown>): Vehicle {
  return {
    id: raw.id as string,
    account_id: ACCOUNT_ID,
    warehouse_id: raw.warehouse_id as string,
    vehicle_type_id: raw.vehicle_type_id as string,
    plate: raw.plate as string,
    current_driver_user_id: raw.current_driver_user_id as string | undefined,
    active: raw.active as boolean,
  };
}

function adaptNotification(raw: Record<string, unknown>): Notification {
  return {
    id: raw.id as string,
    account_id: ACCOUNT_ID,
    target_user_id: raw.target_user_id as string,
    type: raw.type as string,
    severity: raw.severity as Notification["severity"],
    title: raw.title as string,
    body: raw.body as string | undefined,
    related_route_id: raw.related_route_id as string | undefined,
    related_order_id: raw.related_order_id as string | undefined,
    related_driver_id: raw.related_driver_id as string | undefined,
    read_at: raw.read_at as string | undefined,
    created_at: raw.created_at as string,
  };
}

function adaptOrder(raw: Record<string, unknown>): Order {
  return {
    id: raw.id as string,
    warehouse_id: raw.warehouse_id as string,
    client_id: raw.client_id as string,
    client_name: raw.client_name as string | undefined,
    code: raw.code as string,
    delivery_address: raw.delivery_address as string | undefined,
    delivery_lat: raw.delivery_lat as number | undefined,
    delivery_lon: raw.delivery_lon as number | undefined,
    planned_date: raw.planned_date as string,
    service_minutes: (raw.service_minutes as number) ?? 0,
    invoice_count: (raw.invoice_count as number) ?? 0,
    total_amount: (raw.total_amount as number) ?? 0,
    status: raw.status as Order["status"],
    notes: raw.notes as string | undefined,
    placed_at: raw.placed_at as string,
    division: raw.division as string | undefined,
  };
}

function adaptRouteStop(raw: Record<string, unknown>): RouteStopRow {
  const out: RouteStopRow = {
    id: raw.id as string,
    order_id: raw.order_id as string,
    sequence: typeof raw.sequence === "number" ? raw.sequence : null,
    lat: raw.lat as number | undefined,
    lon: raw.lon as number | undefined,
    planned_arrival: raw.planned_arrival as string | undefined,
    planned_service_min: (raw.planned_service_min as number) ?? 0,
    status: raw.status as string,
    client_code: raw.client_code as string | undefined,
    client_name: raw.client_name as string | undefined,
    seller_zone: raw.seller_zone as string | undefined,
    total_amount: raw.total_amount as number | undefined,
  };
  if (raw.delivery && typeof raw.delivery === "object") {
    const d = raw.delivery as Record<string, unknown>;
    const atts = (d.attachments as Record<string, unknown>[] | undefined) ?? [];
    out.delivery = {
      id: d.id as string,
      signed_by: d.signed_by as string | undefined,
      delivered_total: d.delivered_total as number | undefined,
      cod_collected: d.cod_collected as number | undefined,
      notes: d.notes as string | undefined,
      lat: d.lat as number | undefined,
      lon: d.lon as number | undefined,
      proximity_meters: d.proximity_meters as number | undefined,
      delivered_at: d.delivered_at as string | undefined,
      attachments: atts.map((a) => ({
        id: a.id as string,
        kind: a.kind as string,
        url: a.url as string,
      })),
    };
  }
  if (raw.exception && typeof raw.exception === "object") {
    const e = raw.exception as Record<string, unknown>;
    out.exception = {
      id: e.id as string,
      reason_code: e.reason_code as string | undefined,
      reason_label: e.reason_label as string | undefined,
      notes: e.notes as string | undefined,
      lat: e.lat as number | undefined,
      lon: e.lon as number | undefined,
      occurred_at: e.occurred_at as string | undefined,
    };
  }
  return out;
}

function adaptRoute(raw: Record<string, unknown>): Route {
  const stops = (raw.stops as Record<string, unknown>[] | undefined) ?? [];
  return {
    id: raw.id as string,
    warehouse_id: raw.warehouse_id as string,
    code: raw.code as string,
    planned_date: raw.planned_date as string,
    driver_user_id: raw.driver_user_id as string | undefined,
    vehicle_id: raw.vehicle_id as string | undefined,
    status: raw.status as Route["status"],
    planned_distance_km: raw.planned_distance_km as number | undefined,
    planned_duration_h: raw.planned_duration_h as number | undefined,
    planned_cost: raw.planned_cost as number | undefined,
    started_at: raw.started_at as string | undefined,
    finished_at: raw.finished_at as string | undefined,
    stops: stops.map(adaptRouteStop),
  };
}

// --- API methods ---------------------------------------------------------

export const api = {
  // Healthcheck liviano: pide 1 sola fila para confirmar que el engine
  // responde. Usado por el indicador de conexión de la Sidebar.
  async ping(): Promise<boolean> {
    try {
      await getJSON<ListEnvelope<Record<string, unknown>>>("/api/clients?limit=1");
      return true;
    } catch {
      return false;
    }
  },

  async listClients(): Promise<Client[]> {
    const res = await getJSON<ListEnvelope<Record<string, unknown>>>("/api/clients?limit=500");
    return res.data.map(adaptClient);
  },

  async listClientAddresses(clientId: string): Promise<ClientAddress[]> {
    const res = await getJSON<ListEnvelope<Record<string, unknown>>>(
      `/api/clients/${encodeURIComponent(clientId)}/addresses`,
    );
    return res.data.map(adaptClientAddress);
  },

  async listDrivers(): Promise<User[]> {
    const res = await getJSON<ListEnvelope<Record<string, unknown>>>("/api/drivers?limit=500");
    return res.data.map(adaptUser);
  },

  async listUsers(role?: string): Promise<User[]> {
    const qs = role ? `?role=${encodeURIComponent(role)}&limit=500` : "?limit=500";
    const res = await getJSON<ListEnvelope<Record<string, unknown>>>(`/api/users${qs}`);
    return res.data.map(adaptUser);
  },

  async listWarehouses(): Promise<Warehouse[]> {
    const res = await getJSON<ListEnvelope<Record<string, unknown>>>("/api/warehouses?limit=500");
    return res.data.map(adaptWarehouse);
  },

  async listVehicles(): Promise<Vehicle[]> {
    const res = await getJSON<ListEnvelope<Record<string, unknown>>>("/api/vehicles?limit=500");
    return res.data.map(adaptVehicle);
  },

  async listNotifications(targetUserId: string): Promise<Notification[]> {
    const qs = `?target_user_id=${encodeURIComponent(targetUserId)}&limit=500`;
    const res = await getJSON<ListEnvelope<Record<string, unknown>>>(`/api/notifications${qs}`);
    return res.data.map(adaptNotification);
  },

  // ---- Writes (M3.1) -----------------------------------------------------
  //
  // El backend acepta JSON con campos opcionales; el frontend manda
  // solo lo que el usuario tocó. Tipamos los inputs como Partial para
  // que TS exija nombrar lo que se envía y el resto quede implícito.

  async updateClient(id: string, patch: Partial<Client>): Promise<Client> {
    const raw = await sendJSON<Record<string, unknown>>("PATCH", `/api/clients/${encodeURIComponent(id)}`, patch);
    return adaptClient(raw);
  },
  async createClient(c: Partial<Client>): Promise<Client> {
    const raw = await sendJSON<Record<string, unknown>>("POST", `/api/clients`, c);
    return adaptClient(raw);
  },

  async updateWarehouse(id: string, patch: Partial<Warehouse>): Promise<Warehouse> {
    const raw = await sendJSON<Record<string, unknown>>("PATCH", `/api/warehouses/${encodeURIComponent(id)}`, patch);
    return adaptWarehouse(raw);
  },
  async createWarehouse(w: Partial<Warehouse>): Promise<Warehouse> {
    const raw = await sendJSON<Record<string, unknown>>("POST", `/api/warehouses`, w);
    return adaptWarehouse(raw);
  },

  async updateUser(id: string, patch: Partial<User>): Promise<User> {
    const raw = await sendJSON<Record<string, unknown>>("PATCH", `/api/users/${encodeURIComponent(id)}`, patch);
    return adaptUser(raw);
  },
  async createUser(u: Partial<User>): Promise<User> {
    const raw = await sendJSON<Record<string, unknown>>("POST", `/api/users`, u);
    return adaptUser(raw);
  },

  // PATCH /api/vehicles/{id}/driver — asigna/desasigna chofer al
  // vehículo. Cascade: las rutas pendientes del vehículo se reasignan
  // al nuevo chofer en la misma transacción del backend.
  async setVehicleDriver(vehicleId: string, driverUserId: string | null): Promise<void> {
    await sendJSON<unknown>(
      "PATCH",
      `/api/vehicles/${encodeURIComponent(vehicleId)}/driver`,
      { driver_user_id: driverUserId },
    );
  },

  async markNotificationRead(id: string, targetUserId: string): Promise<void> {
    const qs = `?target_user_id=${encodeURIComponent(targetUserId)}`;
    await sendJSON<unknown>("POST", `/api/notifications/${encodeURIComponent(id)}/read${qs}`);
  },
  async markAllNotificationsRead(targetUserId: string): Promise<number> {
    const qs = `?target_user_id=${encodeURIComponent(targetUserId)}`;
    const r = await sendJSON<{ updated: number }>("POST", `/api/notifications/read-all${qs}`);
    return r.updated;
  },

  // ---- Orders / Routes (M3.2) -------------------------------------------

  async listOrders(params: {
    warehouseId?: string;
    clientId?: string;
    status?: string;
    plannedDate?: string;
    /** "CONSUMO" / "SALUD" — case-insensitive; omitido = todas. */
    division?: string;
    limit?: number;
  } = {}): Promise<Order[]> {
    const qs = new URLSearchParams();
    if (params.warehouseId) qs.set("warehouse_id", params.warehouseId);
    if (params.clientId) qs.set("client_id", params.clientId);
    if (params.status) qs.set("status", params.status);
    if (params.plannedDate) qs.set("planned_date", params.plannedDate);
    if (params.division) qs.set("division", params.division);
    // Default 2000 — soporta días de pico de ~1k pedidos con margen
    // (el frontend filtra por fecha y trae el día entero en una sola
    // llamada para evitar paginación en pantalla). El backend tiene
    // cap a maxPageSize = 2000 que coincide.
    qs.set("limit", String(params.limit ?? 2000));
    const res = await getJSON<ListEnvelope<Record<string, unknown>>>(
      `/api/orders?${qs.toString()}`,
    );
    return res.data.map(adaptOrder);
  },

  // GET /api/orders/{id} — devuelve la orden con campos del cliente
  // denormalizados (nombre, dirección, teléfono). Usado por el drawer de
  // detalle de la página de Órdenes.
  async getOrder(id: string): Promise<OrderDetail> {
    const raw = await getJSON<Record<string, unknown>>(
      `/api/orders/${encodeURIComponent(id)}`,
    );
    const rawItems = (raw.items as Record<string, unknown>[] | undefined) ?? [];
    return {
      ...adaptOrder(raw),
      client_name: (raw.client_name as string) ?? "",
      client_address: raw.client_address as string | undefined,
      client_phone: raw.client_phone as string | undefined,
      items: rawItems.map((it) => ({
        id: it.id as string,
        sku: it.sku as string | undefined,
        description: (it.description as string) ?? "",
        quantity: (it.quantity as number) ?? 0,
        unit: (it.unit as string) ?? "unidad",
        unit_price: (it.unit_price as number) ?? 0,
        line_total: (it.line_total as number) ?? 0,
        position: (it.position as number) ?? 0,
      })),
    };
  },

  async listRoutes(params: {
    warehouseId?: string;
    /** Subset de warehouses — el backend intersecta con el scope del caller. */
    warehouseIds?: string[];
    driverUserId?: string;
    fromDate?: string;
    toDate?: string;
    limit?: number;
  } = {}): Promise<Route[]> {
    const qs = new URLSearchParams();
    if (params.warehouseId) qs.set("warehouse_id", params.warehouseId);
    if (params.warehouseIds && params.warehouseIds.length > 0) {
      qs.set("warehouse_ids", params.warehouseIds.join(","));
    }
    if (params.driverUserId) qs.set("driver_user_id", params.driverUserId);
    if (params.fromDate) qs.set("from_date", params.fromDate);
    if (params.toDate) qs.set("to_date", params.toDate);
    qs.set("limit", String(params.limit ?? 500));
    const res = await getJSON<ListEnvelope<Record<string, unknown>>>(
      `/api/routes?${qs.toString()}`,
    );
    return res.data.map(adaptRoute);
  },

  // GET /api/coordinator/team — supervisores del equipo del coordinator
  // actual (resuelto por X-User-ID) + sus warehouses asignados.
  // ---- Asignación de warehouses a supervisores -----------------------------

  /// GET /api/users/{id}/warehouses — almacenes asignados al supervisor.
  /// Lo usa el drawer del supervisor en `/users` (página de usuarios) para
  /// pintar los chips y dejar al admin/coordinator agregar/quitar.
  async listSupervisorWarehouses(supervisorId: string): Promise<Warehouse[]> {
    const res = await getJSON<ListEnvelope<Record<string, unknown>>>(
      `/api/users/${encodeURIComponent(supervisorId)}/warehouses`,
    );
    return res.data.map(adaptWarehouse);
  },

  /// POST /api/users/{id}/warehouses — agrega un warehouse. Idempotente.
  async assignSupervisorWarehouse(
    supervisorId: string,
    warehouseId: string,
  ): Promise<void> {
    await sendJSON<Record<string, unknown>>(
      "POST",
      `/api/users/${encodeURIComponent(supervisorId)}/warehouses`,
      { warehouse_id: warehouseId },
    );
  },

  /// DELETE /api/users/{id}/warehouses/{warehouseId} — desasigna.
  async unassignSupervisorWarehouse(
    supervisorId: string,
    warehouseId: string,
  ): Promise<void> {
    const res = await fetch(
      `/api/users/${encodeURIComponent(supervisorId)}/warehouses/${encodeURIComponent(warehouseId)}`,
      { method: "DELETE", headers: { ...authHeaders() } },
    );
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`API ${res.status} on DELETE: ${text || res.statusText}`);
    }
  },

  // ---- Notification templates (WA: WhatsApp al vendedor en rechazo) -------

  async getNotificationTemplate(kind: string): Promise<{
    enabled: boolean;
    body_template: string;
    updated_at?: string;
  }> {
    const raw = await getJSON<Record<string, unknown>>(
      `/api/notification-templates/${encodeURIComponent(kind)}`,
    );
    return {
      enabled: Boolean(raw.enabled),
      body_template: String(raw.body_template ?? ""),
      updated_at: typeof raw.updated_at === "string" ? raw.updated_at : undefined,
    };
  },

  async upsertNotificationTemplate(
    kind: string,
    payload: { enabled: boolean; body_template: string },
  ): Promise<void> {
    const res = await fetch(
      `/api/notification-templates/${encodeURIComponent(kind)}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`API ${res.status} on PUT template: ${text || res.statusText}`);
    }
  },

  async getCoordinatorTeam(): Promise<SupervisorTeamMember[]> {
    const res = await getJSON<ListEnvelope<SupervisorTeamMember>>(
      "/api/coordinator/team",
    );
    return res.data;
  },

  async getRoute(id: string): Promise<Route> {
    const raw = await getJSON<Record<string, unknown>>(
      `/api/routes/${encodeURIComponent(id)}`,
    );
    return adaptRoute(raw);
  },

  // ---- GPS telemetry (M6) ------------------------------------------------
  //
  // El supervisor poll-ea cada 10-15s para ver dónde está cada chofer.
  // 404 = el chofer todavía no pusheó pings (sesión recién abierta o
  // permiso denegado en el device). El call-site lo trata como "GPS
  // no disponible" en vez de error.

  async getDriverLastGps(driverUserId: string): Promise<GpsPing | null> {
    try {
      return await getJSON<GpsPing>(
        `/api/drivers/${encodeURIComponent(driverUserId)}/last-gps`,
      );
    } catch (e) {
      if (e instanceof Error && e.message.includes("404")) return null;
      throw e;
    }
  },

  // ---- Programación (M8) -------------------------------------------------

  async programacionOptimize(body: ProgramacionOptimizeReq): Promise<ProgramacionOptimizeResp> {
    return sendJSON<ProgramacionOptimizeResp>("POST", "/api/programacion/optimize", body);
  },

  async programacionSave(body: ProgramacionSaveReq): Promise<ProgramacionSaveResp> {
    return sendJSON<ProgramacionSaveResp>("POST", "/api/programacion/save", body);
  },

  // ---- Programación dinámica (M10) ---------------------------------------
  //
  // Toma la asignación inicial del CSV (semilla) + las capacidades de cada
  // transportista y devuelve una redistribución que respeta los límites
  // de tiempo / peso / volumen. Proxy al servicio Python OR-Tools.

  async programacionAssign(body: ProgramacionAssignReq): Promise<ProgramacionAssignResp> {
    return sendJSON<ProgramacionAssignResp>("POST", "/api/programacion/assign-ortools", body);
  },

  // ---- Order batches (M11) ----------------------------------------------
  //
  // Lotes de órdenes. El planner sube un CSV → se crea un batch con N
  // órdenes vinculadas → luego elige qué batches programar. Permite
  // múltiples uploads en el día (sucursales / horas distintas).

  async listOrderBatches(params: {
    fromDate?: string;
    toDate?: string;
    status?: OrderBatchStatus;
    search?: string;
    limit?: number;
  } = {}): Promise<OrderBatch[]> {
    const qs = new URLSearchParams();
    if (params.fromDate) qs.set("from_date", params.fromDate);
    if (params.toDate) qs.set("to_date", params.toDate);
    if (params.status) qs.set("status", params.status);
    if (params.search) qs.set("search", params.search);
    qs.set("limit", String(params.limit ?? 200));
    const res = await getJSON<ListEnvelope<Record<string, unknown>>>(
      `/api/order-batches?${qs.toString()}`,
    );
    return res.data.map(adaptOrderBatch);
  },

  async uploadOrderBatchCsv(body: UploadOrderBatchCsvReq): Promise<UploadOrderBatchCsvResp> {
    const raw = await sendJSON<{ batch: Record<string, unknown>; created: number; skipped: number }>(
      "POST",
      "/api/order-batches/csv",
      body,
    );
    return {
      batch: adaptOrderBatch(raw.batch),
      created: raw.created,
      skipped: raw.skipped,
    };
  },

  async updateOrderBatchStatus(id: string, status: OrderBatchStatus): Promise<void> {
    await sendJSON<{ status: string }>(
      "POST",
      `/api/order-batches/${encodeURIComponent(id)}/status`,
      { status },
    );
  },

  // Hidrata las órdenes de uno o más batches para el optimizador. Cada
  // fila tiene lo que SaleRow necesita; el caller lo adapta a su shape.
  async listOrdersByBatchIds(batchIds: string[]): Promise<BatchOrderRow[]> {
    if (batchIds.length === 0) return [];
    const qs = `batch_ids=${batchIds.map(encodeURIComponent).join(",")}`;
    const res = await getJSON<ListEnvelope<BatchOrderRow>>(
      `/api/order-batches/orders?${qs}`,
    );
    return res.data;
  },

  // ---- Optimizer config profiles (M11.5) --------------------------------

  async listOptimizerConfigs(): Promise<OptimizerConfigProfile[]> {
    const res = await getJSON<ListEnvelope<OptimizerConfigProfile>>(
      "/api/optimizer-configs",
    );
    return res.data;
  },

  async getOptimizerConfig(id: string): Promise<OptimizerConfigProfile> {
    return getJSON<OptimizerConfigProfile>(
      `/api/optimizer-configs/${encodeURIComponent(id)}`,
    );
  },

  async createOptimizerConfig(body: CreateOptimizerConfigReq): Promise<OptimizerConfigProfile> {
    return sendJSON<OptimizerConfigProfile>("POST", "/api/optimizer-configs", body);
  },

  async updateOptimizerConfig(id: string, body: UpdateOptimizerConfigReq): Promise<OptimizerConfigProfile> {
    return sendJSON<OptimizerConfigProfile>(
      "PATCH",
      `/api/optimizer-configs/${encodeURIComponent(id)}`,
      body,
    );
  },

  // ---- Last-Mile import (M12) -------------------------------------------
  //
  // Recibe el Excel parseado por el frontend (en formato del backend
  // DTO: campos snake_case, fechas ISO, lat/lon como números). El backend
  // procesa todo en una transacción y devuelve los counts created/updated.

  async lastMileImport(rows: unknown[]): Promise<LastMileImportResult> {
    return sendJSON<LastMileImportResult>("POST", "/api/last-mile/import", { rows });
  },

  /// Historial de imports del tenant (más recientes primero). Solo admin.
  async listLastMileImports(): Promise<LastMileImportSummary[]> {
    const res = await getJSON<ListEnvelope<LastMileImportSummary>>(
      "/api/last-mile/imports/",
    );
    return res.data;
  },

  /// Borra un import + toda su data (rutas, órdenes, sales_documents,
  /// deliveries colaterales). Maestros NO se tocan. Solo admin.
  async deleteLastMileImport(id: string): Promise<LastMileImportDeleteResult> {
    return sendJSON<LastMileImportDeleteResult>(
      "DELETE",
      `/api/last-mile/imports/${encodeURIComponent(id)}`,
    );
  },

  async deleteOptimizerConfig(id: string): Promise<void> {
    // DELETE devuelve 204 No Content; usamos fetch directo para no romper
    // el contrato JSON de sendJSON (que asume body en respuesta).
    const res = await fetch(`/api/optimizer-configs/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { ...authHeaders() },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`API ${res.status} on DELETE: ${text || res.statusText}`);
    }
  },

  // ---- Avance del repartidor (SALUD) ---------------------------------------

  /// GET /api/repartidor/avance — alimenta las pantallas de supervisión
  /// cuando el toggle está en SALUD.
  async getRepartidorAvance(days = 3): Promise<RepartidorAvanceResponse> {
    const raw = await getJSON<Record<string, unknown>>(
      `/api/repartidor/avance?days=${days}`,
    );
    const repartidores = (raw.repartidores as Record<string, unknown>[] | undefined) ?? [];
    const summary = (raw.summary as Record<string, unknown> | undefined) ?? {};
    return {
      days: (raw.days as number) ?? days,
      repartidores: repartidores.map((r) => ({
        user_id: r.user_id as string,
        full_name: (r.full_name as string) ?? "",
        dni: (r.dni as string) ?? "",
        total: (r.total as number) ?? 0,
        pending: (r.pending as number) ?? 0,
        delivered: (r.delivered as number) ?? 0,
        partial: (r.partial as number) ?? 0,
        failed: (r.failed as number) ?? 0,
        last_activity_at: r.last_activity_at as string | undefined,
        last_lat: r.last_lat as number | undefined,
        last_lon: r.last_lon as number | undefined,
      })),
      summary: {
        total_orders: (summary.total_orders as number) ?? 0,
        pending: (summary.pending as number) ?? 0,
        delivered: (summary.delivered as number) ?? 0,
        partial: (summary.partial as number) ?? 0,
        failed: (summary.failed as number) ?? 0,
        repartidores: (summary.repartidores as number) ?? 0,
      },
    };
  },

  /// GET /api/orders/{id}/pod-history — historial completo de POD para
  /// una orden, incluyendo registros deshechos. Lo consume el drawer de
  /// OrdersPage para mostrar auditoría al supervisor/admin.
  async getOrderPODHistory(orderId: string): Promise<PODHistoryEntry[]> {
    const res = await getJSON<ListEnvelope<Record<string, unknown>>>(
      `/api/orders/${encodeURIComponent(orderId)}/pod-history`,
    );
    return res.data.map((e) => ({
      id: e.id as string,
      kind: e.kind as "delivery" | "exception",
      occurred_at: e.occurred_at as string,
      actor_user_id: (e.actor_user_id as string) ?? "",
      actor_name: (e.actor_name as string) ?? "",
      reason_code: (e.reason_code as string) ?? "",
      reason_label: (e.reason_label as string) ?? "",
      notes: (e.notes as string) ?? "",
      delivered_total: e.delivered_total as number | undefined,
      cod_collected: e.cod_collected as number | undefined,
      voided: e.voided === true,
      voided_at: e.voided_at as string | undefined,
      voided_by_user_id: (e.voided_by_user_id as string) ?? "",
      voided_by_name: (e.voided_by_name as string) ?? "",
    }));
  },

  /// GET /api/repartidor/{id}/avance-stops — drill-down de un repartidor.
  async getRepartidorAvanceStops(
    userId: string,
    days = 3,
  ): Promise<AvanceStop[]> {
    const res = await getJSON<ListEnvelope<Record<string, unknown>>>(
      `/api/repartidor/${encodeURIComponent(userId)}/avance-stops?days=${days}`,
    );
    return res.data.map((s) => ({
      stop_id: s.stop_id as string,
      order_id: s.order_id as string,
      order_code: (s.order_code as string) ?? "",
      client_name: (s.client_name as string) ?? "",
      stop_status: (s.stop_status as string) ?? "",
      lat: s.lat as number | undefined,
      lon: s.lon as number | undefined,
      updated_at: (s.updated_at as string) ?? "",
      total_amount: (s.total_amount as number) ?? 0,
    }));
  },
};

// ---- Tipos del historial de POD ------------------------------------------

export type PODHistoryEntry = {
  id: string;
  kind: "delivery" | "exception";
  occurred_at: string;
  actor_user_id: string;
  actor_name: string;
  reason_code: string;
  reason_label: string;
  notes: string;
  delivered_total?: number;
  cod_collected?: number;
  voided: boolean;
  voided_at?: string;
  voided_by_user_id: string;
  voided_by_name: string;
};

// ---- Tipos del avance SALUD ----------------------------------------------

export type RepartidorAvance = {
  user_id: string;
  full_name: string;
  dni: string;
  total: number;
  pending: number;
  delivered: number;
  partial: number;
  failed: number;
  last_activity_at?: string;
  last_lat?: number;
  last_lon?: number;
};

export type RepartidorAvanceSummary = {
  total_orders: number;
  pending: number;
  delivered: number;
  partial: number;
  failed: number;
  repartidores: number;
};

export type RepartidorAvanceResponse = {
  days: number;
  repartidores: RepartidorAvance[];
  summary: RepartidorAvanceSummary;
};

export type AvanceStop = {
  stop_id: string;
  order_id: string;
  order_code: string;
  client_name: string;
  stop_status: string;
  lat?: number;
  lon?: number;
  updated_at: string;
  total_amount: number;
};

// Adapter local — la implementación vive en features/programacion/orderBatchMapping
// para soportar tests unitarios sin depender de fetch.
const adaptOrderBatch = orderBatchAdapter;

// ---- Programación DTOs (cliente-side) ------------------------------------

export type ProgramacionOptimizeReq = {
  depot_lat?: number;
  depot_lon?: number;
  carriers: {
    code: string;
    name: string;
    stops: { id: number; lat: number; lon: number }[];
  }[];
};

export type ProgramacionOptimizeResp = {
  carriers: {
    code: string;
    order: number[];
    polyline?: [number, number][];
    distance_m: number;
    duration_s: number;
    error?: string;
  }[];
};

export type ProgramacionSaveReq = {
  warehouse_id?: string;
  planned_date: string;
  carriers: {
    code: string;
    name: string;
    distance_m: number;
    duration_s: number;
    order: {
      id: number;
      lat: number;
      lon: number;
      cliente: string;
      cod_cliente: string;
      valor_venta: number;
    }[];
  }[];
};

export type ProgramacionSaveResp = {
  created: number;
  mapped: number;
  unmapped: number;
  routes: {
    code: string;
    route_id: string;
    driver_code: string;
    driver_user_id?: string;
    mapped: boolean;
    stops: number;
  }[];
};

// --- M10: asignación dinámica multi-restricción ---

export type ProgramacionAssignReq = {
  carriers: {
    code: string;
    /** kg máx nominal del vehículo. Si falta el backend usa 500. */
    max_kg?: number;
    /** m³ máx nominal del vehículo. Si falta el backend usa 4. */
    max_m3?: number;
  }[];
  rows: {
    id: number;
    cod_cliente?: string;
    lat: number;
    lon: number;
    valor_venta: number;
    peso_kg?: number;
    volumen_m3?: number;
    has_peso: boolean;
    has_volumen: boolean;
    current_carrier_code: string;
  }[];
  limits: {
    max_hours: number;
    max_weight_pct: number;
    max_volume_pct: number;
    use_weight?: boolean;
    use_volume?: boolean;
    use_sales?: boolean;
  };
  fallbacks: {
    service_minutes: number;
    kg_per_sol: number;
    m3_per_sol: number;
  };
  stability_penalty?: number;
};

export type ProgramacionAssignResp = {
  assignments: { row_id: number; carrier_code: string; sequence: number }[];
  unassigned: { row_id: number; reason: string }[];
  utilization: {
    carrier_code: string;
    stops: number;
    load_kg: number;
    load_m3: number;
    time_h: number;
    max_kg: number;
    max_m3: number;
    max_hours: number;
    weight_pct: number;
    volume_pct: number;
    time_pct: number;
  }[];
  total_distance_km: number;
  variability_pct: number;
  cv_weight_pct: number;
  cv_volume_pct: number;
  cv_time_pct: number;
  changes_from_initial: number;
  iterations: number;
  duration_ms: number;
};
